import React, { useState, useEffect } from 'react';
import { Label } from '../types/board';
import { supabase } from '../services/supabase';
import './WordPressSiteUrlEditor.css';

interface WordPressSiteUrlEditorProps {
  label: Label;
  onClose: () => void;
  onSave: () => void;
}

interface WordPressSite {
  id?: string;
  label_id: string;
  label_name: string;
  wordpress_url: string;
  username: string;
  app_password: string;
  is_active: boolean;
}

const WordPressSiteUrlEditor: React.FC<WordPressSiteUrlEditorProps> = ({
  label,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<WordPressSite>({
    label_id: label.id,
    label_name: label.name,
    wordpress_url: '',
    username: '',
    app_password: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWordPressSite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label.id]);

  const loadWordPressSite = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('wordpress_sites')
        .select('*')
        .eq('label_id', label.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setFormData(data);
      }
    } catch (err) {
      console.error('Error loading WordPress site:', err);
      setError(err instanceof Error ? err.message : 'Failed to load WordPress site data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Validate URL format
      if (formData.wordpress_url && !formData.wordpress_url.startsWith('http')) {
        throw new Error('WordPress URL must start with http:// or https://');
      }

      const dataToSave = {
        label_id: formData.label_id,
        label_name: formData.label_name,
        wordpress_url: formData.wordpress_url.trim(),
        username: formData.username.trim(),
        app_password: formData.app_password.trim(),
        is_active: formData.is_active,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('wordpress_sites')
        .upsert(dataToSave, {
          onConflict: 'label_id',
        });

      if (error) throw error;

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving WordPress site:', err);
      setError(err instanceof Error ? err.message : 'Failed to save WordPress site');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.id) return;

    const confirmed = window.confirm(
      `Are you sure you want to remove the WordPress URL for "${label.name}"?`
    );

    if (!confirmed) return;

    setSaving(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('wordpress_sites')
        .delete()
        .eq('label_id', label.id);

      if (error) throw error;

      onSave();
      onClose();
    } catch (err) {
      console.error('Error deleting WordPress site:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete WordPress site');
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="wp-url-editor-overlay" onClick={onClose}>
      <div className="wp-url-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wp-url-editor-header">
          <div className="wp-url-editor-title">
            <span
              className="label-dot"
              style={{ backgroundColor: label.color }}
            ></span>
            <h3>WordPress Settings: {label.name}</h3>
          </div>
          <button className="wp-url-editor-close" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <div className="wp-url-editor-loading">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="wp-url-editor-content">
              {error && (
                <div className="wp-url-editor-error">
                  <strong>Error:</strong> {error}
                </div>
              )}

              <div className="wp-url-editor-field">
                <label htmlFor="wordpress_url">
                  WordPress URL <span className="required">*</span>
                </label>
                <input
                  type="url"
                  id="wordpress_url"
                  placeholder="https://yoursite.com"
                  value={formData.wordpress_url}
                  onChange={(e) =>
                    setFormData({ ...formData, wordpress_url: e.target.value })
                  }
                  required
                />
                <p className="field-hint">
                  The base URL of your WordPress site (e.g., https://example.com)
                </p>
              </div>

              <div className="wp-url-editor-field">
                <label htmlFor="username">
                  WordPress Username <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="username"
                  placeholder="your-username"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  required
                />
                <p className="field-hint">Your WordPress admin username</p>
              </div>

              <div className="wp-url-editor-field">
                <label htmlFor="app_password">
                  Application Password <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="app_password"
                  placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                  value={formData.app_password}
                  onChange={(e) =>
                    setFormData({ ...formData, app_password: e.target.value })
                  }
                  required
                />
                <p className="field-hint">
                  Generate at: WordPress Admin → Users → Profile → Application
                  Passwords
                </p>
              </div>

              <div className="wp-url-editor-field checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                  <span>Active (enable syncing for this site)</span>
                </label>
              </div>

              <div className="wp-url-editor-note">
                <strong>Note:</strong> These credentials are stored in your
                Supabase database. Make sure your database has proper security
                policies in place.
              </div>
            </div>

            <div className="wp-url-editor-footer">
              <div className="footer-left">
                {formData.id && (
                  <button
                    type="button"
                    className="wp-url-editor-delete"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="footer-right">
                <button
                  type="button"
                  className="wp-url-editor-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="wp-url-editor-save"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default WordPressSiteUrlEditor;
